<template>
  <div class="contact-wrapper">
    <div class="contact-container">
      <!-- FORM -->
      <div class="contact-form">
        <div class="contact-form-header">
          <h1 class="contact-title">{{ $t('contact.header.title') }}</h1>
          <p class="contact-subtitle">{{ $t('contact.header.description') }}</p>
        </div>

        <form class="form-body" @submit.prevent="submit" novalidate>
          <div class="form-two">
            <div class="field">
              <label for="firstName">{{ $t('contact.form.firstName.label') }}</label>
              <input id="firstName" type="text" v-model.trim="firstName"
                :class="{ invalid: showError('firstName') }" @blur="touch('firstName')"
                :placeholder="$t('contact.form.firstName.placeholder')" />
              <span v-if="showError('firstName')" class="field-error">Requerido.</span>
            </div>
            <div class="field">
              <label for="lastName">{{ $t('contact.form.lastName.label') }}</label>
              <input id="lastName" type="text" v-model.trim="lastName"
                :class="{ invalid: showError('lastName') }" @blur="touch('lastName')"
                :placeholder="$t('contact.form.lastName.placeholder')" />
              <span v-if="showError('lastName')" class="field-error">Requerido.</span>
            </div>
          </div>

          <div class="field">
            <label for="companyName">{{ $t('contact.form.companyName.label') }}</label>
            <input id="companyName" type="text" v-model.trim="companyName"
              :class="{ invalid: showError('companyName') }" @blur="touch('companyName')"
              :placeholder="$t('contact.form.companyName.placeholder')" />
            <span v-if="showError('companyName')" class="field-error">Requerido.</span>
          </div>

          <div class="field">
            <label for="email">{{ $t('contact.form.email.label') }}</label>
            <input id="email" type="email" v-model.trim="email"
              :class="{ invalid: showError('email') }" @blur="touch('email')"
              :placeholder="$t('contact.form.email.placeholder')" />
            <span v-if="showError('email')" class="field-error">Email inválido.</span>
          </div>

          <div class="field">
            <label for="phone">{{ $t('contact.form.phone.label') }}</label>
            <div class="phone-row">
              <select v-model="selectedCountryCode" class="country-select">
                <option v-for="(c, i) in countries" :key="i" :value="c.code">({{ c.code }}) {{ c.name }}</option>
              </select>
              <input id="phone" type="tel" v-model.trim="phoneNumber"
                :class="{ invalid: showError('phone') }" @blur="touch('phone')"
                :placeholder="$t('contact.form.phone.placeholder')" />
            </div>
            <span v-if="showError('phone')" class="field-error">Ingresa un teléfono válido.</span>
          </div>

          <div class="field">
            <label for="message">{{ $t('contact.form.message.label') }}</label>
            <textarea id="message" v-model.trim="message" rows="3"
              :class="{ invalid: showError('message') }" @blur="touch('message')"
              :placeholder="$t('contact.form.message.placeholder')"></textarea>
            <span v-if="showError('message')" class="field-error">Requerido.</span>
          </div>

          <a class="policy-link" href="/Política de Tratamiento de Datos Personales.pdf" target="_blank">
            Al enviar tu mensaje aceptas nuestra política de tratamiento de datos
          </a>

          <button class="send" type="submit" :disabled="isSubmitting">
            <span v-if="!isSubmitting">{{ $t('contact.form.button') }}</span>
            <span v-else>Enviando...</span>
          </button>

          <div v-if="errorMessage" class="form-message error">{{ errorMessage }}</div>
          <div v-if="sent" class="form-message success">
            ¡Mensaje enviado! Te responderemos pronto.
          </div>
        </form>
      </div>

      <!-- IMAGEN -->
      <div class="contact-image"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'

const countries = [
  { name: 'Colombia', code: '+57' },
  { name: 'United States', code: '+1' },
  { name: 'Mexico', code: '+52' },
  { name: 'Argentina', code: '+54' },
  { name: 'Brazil', code: '+55' },
  { name: 'Germany', code: '+49' },
  { name: 'Spain', code: '+34' },
  { name: 'France', code: '+33' },
  { name: 'United Kingdom', code: '+44' },
  { name: 'Japan', code: '+81' },
  { name: 'South Korea', code: '+82' },
  { name: 'China', code: '+86' },
  { name: 'India', code: '+91' },
  { name: 'Australia', code: '+61' },
]

const firstName = ref('')
const lastName = ref('')
const companyName = ref('')
const email = ref('')
const selectedCountryCode = ref('+57')
const phoneNumber = ref('')
const message = ref('')

const isSubmitting = ref(false)
const sent = ref(false)
const errorMessage = ref('')
const submitted = ref(false)

type FieldKey = 'firstName' | 'lastName' | 'companyName' | 'email' | 'phone' | 'message'
const touched = reactive<Record<FieldKey, boolean>>({
  firstName: false, lastName: false, companyName: false, email: false, phone: false, message: false,
})

const validity = computed<Record<FieldKey, boolean>>(() => ({
  firstName: firstName.value.length > 0,
  lastName: lastName.value.length > 0,
  companyName: companyName.value.length > 0,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value),
  phone: /^[0-9]{6,}$/.test(phoneNumber.value),
  message: message.value.length > 0,
}))

const formValid = computed(() => Object.values(validity.value).every(Boolean))

const touch = (k: FieldKey) => { touched[k] = true }
const showError = (k: FieldKey) => (touched[k] || submitted.value) && !validity.value[k]

const submit = async () => {
  submitted.value = true
  errorMessage.value = ''
  sent.value = false
  if (!formValid.value) {
    errorMessage.value = 'Revisa los campos marcados.'
    return
  }

  isSubmitting.value = true
  try {
    // Persistir en Supabase
    const { submitContactMessage } = await import('../lib/supabase')
    await submitContactMessage({
      first_name: firstName.value,
      last_name: lastName.value,
      company: companyName.value,
      email: email.value,
      phone: selectedCountryCode.value + phoneNumber.value,
      message: message.value,
    })

    sent.value = true
    firstName.value = lastName.value = companyName.value = email.value = phoneNumber.value = message.value = ''
    submitted.value = false
    ;(Object.keys(touched) as FieldKey[]).forEach((k) => (touched[k] = false))
  } catch (error) {
    console.error(error)
    errorMessage.value = 'Ocurrió un error al enviar el mensaje. Inténtalo nuevamente.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.contact-wrapper {
  font-family: 'Manrope', sans-serif;
  background: #ffffff;
}

.contact-container {
  flex-direction: column-reverse;
  display: flex;
  padding: 48px;
  gap: 32px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Form */
.contact-form {
  background: #ffffff;
  border: 1px solid #e6ecf2;
  border-radius: 16px;
  padding: 40px;
}

.contact-form-header {
  margin-bottom: 28px;
}

.contact-title {
  font-size: 36px;
  font-weight: 800;
  color: #0f1b2e;
  margin: 0 0 8px;
  line-height: 1.1;
}

.contact-subtitle {
  font-size: 18px;
  font-weight: 500;
  color: #6b7a8d;
  margin: 0;
}

.form-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 14px;
  font-weight: 600;
  color: #0f1b2e;
}

.field input,
.field select,
.field textarea {
  font-family: inherit;
  font-size: 16px;
  color: #0f1b2e;
  background: #ffffff;
  border: 1px solid #d9e1ea;
  border-radius: 10px;
  padding: 12px 14px;
  transition: border-color 0.2s ease;
}

.field input::placeholder,
.field textarea::placeholder {
  color: #9aa7b5;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: #129bef;
}

.field input.invalid,
.field textarea.invalid {
  border-color: #ef4444;
}

.field textarea {
  resize: vertical;
  min-height: 90px;
}

.field-error {
  font-size: 13px;
  font-weight: 500;
  color: #dc2626;
}

.phone-row {
  display: flex;
  gap: 10px;
}

.country-select {
  flex: none;
  width: 150px;
}

.phone-row input {
  flex: 1;
}

.policy-link {
  font-size: 13px;
  color: #6b7a8d;
  text-decoration: underline;
}

.policy-link:hover {
  color: #129bef;
}

.send {
  background: #129bef;
  color: #ffffff;
  height: 50px;
  width: 100%;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.send:hover {
  background: #0c82ce;
}

.send:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-message {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

.form-message.error {
  background: #fff1f1;
  color: #dc2626;
}

.form-message.success {
  background: #f0fdf4;
  color: #16a34a;
}

/* Imagen */
.contact-image {
  background-image: url(../assets/images/bannerContact.png);
background-size: cover;
    background-position: center;
    width: 100%;
    height: 170px;
    border-radius: 16px;
    border: 1px solid #e6ecf2;
}

@media (max-width: 768px) {
  .contact-container {
    flex-direction: column;
    padding: 24px 16px;
    gap: 20px;
  }

  .contact-form {
    width: auto;
    padding: 28px 20px;
  }

  .contact-image {
    display: none;
  }

  .form-two {
    grid-template-columns: 1fr;
  }

  .phone-row {
    flex-direction: column;
  }

  .country-select {
    width: 100%;
  }
}
</style>
